import React, { useState, useEffect, useRef } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import {
  FiSearch, FiX, FiFilter, FiChevronDown, FiCalendar, FiStar,
  FiUser, FiMail, FiPhone, FiBriefcase, FiMapPin, FiImage,
  FiPlayCircle, FiMic, FiArrowRight, FiGrid, FiRefreshCw, FiAlertCircle,
  FiCheckCircle, FiClock, FiUserCheck, FiTool, FiMessageSquare
} from "react-icons/fi";

const MEDIA_BASE = "https://api.ajplservices.tech/uploads/tickets/";
const PROFILE_BASE = "https://api.ajplservices.tech/uploads/profile/";

const AdminTicketSearch = () => {
  // Search & filters
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rating, setRating] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("DESC");

  // Data
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ statuses: [], categories: [] });
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => { fetchFilterOptions(); }, []);

  const fetchFilterOptions = async () => {
    try {
      const res = await AdminService.getTicketFilterOptions();
      if (res.success) setFilterOptions(res.data);
    } catch (err) {
      console.error("Filter options error:", err);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await AdminService.searchTickets({
        searchTerm, status, category, dateFrom, dateTo, rating, sort, order
      });
      if (res.success) setTickets(res.data);
    } catch (err) {
      console.error("Search error:", err);
      alert("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSelect = async (ticket) => {
    setDetailsLoading(true);
    try {
      const res = await AdminService.getTicketDetails(ticket.id);
      if (res.success) setSelectedTicket(res.data);
    } catch (err) {
      console.error("Details error:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatus("");
    setCategory("");
    setDateFrom("");
    setDateTo("");
    setRating("");
    setSort("created_at");
    setOrder("DESC");
    setTickets([]);
    setSelectedTicket(null);
  };

  const buildImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const clean = path.split("/").pop();
    return `${MEDIA_BASE}${clean}`;
  };

  const buildProfileUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const clean = path.split("/").pop();
    return `${PROFILE_BASE}${clean}`;
  };

  return (
    <div style={s.layout}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        * { box-sizing: border-box; }

        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }

        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 99px; }
        ::-webkit-scrollbar-thumb:hover { background: #CBD5E1; }

        .ticket-card {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .ticket-card:hover {
          background: #F8FAFF !important;
          transform: translateX(3px);
          box-shadow: 0 4px 20px rgba(66, 42, 251, 0.08) !important;
        }
        .ticket-card.selected {
          background: linear-gradient(135deg, #F0F4FF 0%, #EEF2FF 100%) !important;
          border-left: 3px solid #D4AF37 !important;
          box-shadow: 0 4px 24px rgba(212, 175, 55, 0.15) !important;
        }

        .search-input:focus {
          border-color: #D4AF37 !important;
          box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.1) !important;
          outline: none !important;
        }
        .filter-select:focus {
          border-color: #D4AF37 !important;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1) !important;
          outline: none !important;
        }
        .filter-select:hover { border-color: #D4AF37 !important; }

        .search-btn:hover {
          background: #141C45 !important;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(27, 37, 89, 0.35) !important;
        }
        .search-btn:active { transform: translateY(0) !important; }

        .clear-btn:hover {
          background: #FEF3C7 !important;
          color: #92400E !important;
          border-color: #FDE68A !important;
        }

        .media-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.1) !important;
          border-color: #D4AF37 !important;
        }

        .detail-section-card {
          transition: box-shadow 0.2s ease;
        }
        .detail-section-card:hover {
          box-shadow: 0 4px 20px rgba(0,0,0,0.06) !important;
        }
      `}</style>

      <AdminSidebar />
      <div style={s.main}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div style={s.header}>
          <div style={s.headerLeft}>
            <div style={s.breadcrumb}>
              <FiGrid size={10} color="#D4AF37"/>
              <span style={{color:"#D4AF37", fontWeight:700}}>Admin</span>
              <FiArrowRight size={9} style={{color:"#CBD5E1"}}/>
              <span>Ticket Search</span>
            </div>
            <h1 style={s.title}>Ticket Search</h1>
            <p style={s.subtitle}>Find and manage tickets with advanced filters and full details</p>
          </div>
        </div>

        {/* ── SEARCH & FILTER SECTION ─────────────────────────────────────── */}
        <div style={s.searchCard}>
          {/* Search Bar */}
          <div style={s.searchRow}>
            <div style={s.searchBox}>
              <FiSearch size={15} style={s.searchIcon}/>
              <input
                className="search-input"
                style={s.searchInput}
                placeholder="Search by ticket code, description, employee name, email, or category..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyPress={e => e.key === "Enter" && handleSearch()}
              />
              {searchTerm && (
                <button style={s.clearXBtn} onClick={() => setSearchTerm("")}>
                  <FiX size={12}/>
                </button>
              )}
            </div>
            <button
              className="search-btn"
              style={s.searchBtn}
              onClick={handleSearch}
              disabled={loading}
            >
              <FiRefreshCw size={13} style={loading ? {animation:"spin .8s linear infinite"} : {}}/>
              {loading ? "Searching..." : "Search"}
            </button>
          </div>

          {/* Divider */}
          <div style={s.filterDivider}/>

          {/* Filters Row */}
          <div style={s.filterRow}>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Status</label>
              <select className="filter-select" style={s.filterSelect} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">All Status</option>
                {filterOptions.statuses.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Category</label>
              <select className="filter-select" style={s.filterSelect} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">All Categories</option>
                {filterOptions.categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Rating</label>
              <select className="filter-select" style={s.filterSelect} value={rating} onChange={e => setRating(e.target.value)}>
                <option value="">All Ratings</option>
                <option value="0">Not Rated</option>
                <option value="1to3">1–3 Stars</option>
                <option value="4to5">4–5 Stars</option>
              </select>
            </div>

            <div style={s.filterGroup}>
              <label style={s.filterLabel}>From Date</label>
              <input className="filter-select" style={s.filterDate} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}/>
            </div>

            <div style={s.filterGroup}>
              <label style={s.filterLabel}>To Date</label>
              <input className="filter-select" style={s.filterDate} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}/>
            </div>

            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Sort By</label>
              <select className="filter-select" style={s.filterSelect} value={`${sort}-${order}`} onChange={e => {
                const [sv, ov] = e.target.value.split("-");
                setSort(sv);
                setOrder(ov);
              }}>
                <option value="created_at-DESC">Newest First</option>
                <option value="created_at-ASC">Oldest First</option>
                <option value="ticket_code-ASC">Ticket Code ↑</option>
                <option value="rating-DESC">Highest Rating</option>
              </select>
            </div>

            <div style={{...s.filterGroup, justifyContent:"flex-end"}}>
              <label style={{...s.filterLabel, opacity:0}}>_</label>
              <button className="clear-btn" style={s.clearFilterBtn} onClick={clearFilters}>
                <FiX size={12}/> Clear All
              </button>
            </div>
          </div>
        </div>

        {/* ── LAYOUT: List + Details ───────────────────────────────────────── */}
        <div style={s.contentGrid}>

          {/* LEFT: Ticket List */}
          <div style={s.listPanel}>
            <div style={s.listHeader}>
              <div style={s.listHeaderLeft}>
                <span style={s.listTitle}>Results</span>
                <span style={s.listCount}>{tickets.length}</span>
              </div>
              {selectedTicket && (
                <span style={s.listSelected}>
                  <FiCheckCircle size={10} color="#10B981"/> {selectedTicket.ticket_code}
                </span>
              )}
            </div>

            <div style={s.ticketList}>
              {tickets.length === 0 ? (
                <div style={s.emptyState}>
                  <div style={s.emptyIcon}>
                    <FiSearch size={20} color="#D4AF37"/>
                  </div>
                  <div style={s.emptyTitle}>No results yet</div>
                  <div style={s.emptySubtitle}>Apply filters above and hit Search</div>
                </div>
              ) : (
                tickets.map((ticket, idx) => (
                  <div
                    key={ticket.id}
                    className={`ticket-card${selectedTicket?.id === ticket.id ? " selected" : ""}`}
                    style={{
                      ...s.ticketCard,
                      borderLeft: selectedTicket?.id === ticket.id ? "3px solid #D4AF37" : "3px solid transparent",
                      animationDelay: `${idx * 0.03}s`,
                      animation: "slideUp 0.3s ease both",
                    }}
                    onClick={() => handleTicketSelect(ticket)}
                  >
                    <div style={s.ticketCardTop}>
                      <div style={s.ticketCodeBadge}>
                        <span style={s.ticketCodeText}>{ticket.ticket_code}</span>
                      </div>
                      <StatusBadge status={ticket.status}/>
                    </div>
                    <div style={s.ticketDesc}>{ticket.description?.substring(0, 60)}...</div>
                    <div style={s.ticketMeta}>
                      <span style={s.ticketMetaItem}>
                        <FiUser size={9}/> {ticket.user_name || "Unknown"}
                      </span>
                      <span style={s.ticketMetaItem}>
                        <FiGrid size={9}/> {ticket.category_name}
                      </span>
                      <span style={s.ticketMetaDate}>
                        {new Date(ticket.created_at).toLocaleDateString("en-IN")}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* RIGHT: Details Panel */}
          <div style={s.detailsPanel}>
            {detailsLoading ? (
              <div style={s.loadingState}>
                <div style={s.spinnerRing}/>
                <div style={s.loadingText}>Loading ticket details...</div>
              </div>
            ) : selectedTicket ? (
              <TicketDetailsView
                ticket={selectedTicket}
                buildImageUrl={buildImageUrl}
                buildProfileUrl={buildProfileUrl}
              />
            ) : (
              <div style={s.placeholderState}>
                <div style={s.placeholderIcon}>
                  <FiMessageSquare size={22} color="#D4AF37"/>
                </div>
                <div style={s.placeholderTitle}>Select a ticket</div>
                <div style={s.placeholderSubtitle}>Click any ticket from the list to view its complete details</div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

/* ─── STATUS BADGE ────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
const map = {
  "Pending": {
    bg: "linear-gradient(135deg,#FEF3C7,#FDE68A)",
    txt: "#92400E",
    dot: "#F59E0B",
    border: "rgba(245,158,11,0.2)"
  },

  "In Queue": {
    bg: "linear-gradient(135deg,#FFEDD5,#FED7AA)",
    txt: "#C2410C",
    dot: "#EA580C",
    border: "rgba(234,88,12,0.2)"
  },

  "Work in Progress": {
    bg: "linear-gradient(135deg,#DBEAFE,#BFDBFE)",
    txt: "#1D4ED8",
    dot: "#3B82F6",
    border: "rgba(59,130,246,0.2)"
  },

  "Next Week": {
    bg: "linear-gradient(135deg,#E0F2FE,#BAE6FD)",
    txt: "#0369A1",
    dot: "#0EA5E9",
    border: "rgba(14,165,233,0.2)"
  },

  "Next Month": {
    bg: "linear-gradient(135deg,#F3E8FF,#E9D5FF)",
    txt: "#7E22CE",
    dot: "#A855F7",
    border: "rgba(168,85,247,0.2)"
  },

  "Next Quarter": {
    bg: "linear-gradient(135deg,#EDE9FE,#DDD6FE)",
    txt: "#5B21B6",
    dot: "#7C3AED",
    border: "rgba(124,58,237,0.2)"
  },

  "Resolved": {
    bg: "linear-gradient(135deg,#D1FAE5,#A7F3D0)",
    txt: "#065F46",
    dot: "#10B981",
    border: "rgba(16,185,129,0.2)"
  },

  "Rejected": {
    bg: "linear-gradient(135deg,#FEE2E2,#FECACA)",
    txt: "#991B1B",
    dot: "#EF4444",
    border: "rgba(239,68,68,0.2)"
  },

  "Hold": {
    bg: "linear-gradient(135deg,#EDE9FE,#DDD6FE)",
    txt: "#4C1D95",
    dot: "#8B5CF6",
    border: "rgba(139,92,246,0.2)"
  },
};
  const c = map[status] || { bg:"#F3F4F6", txt:"#374151", dot:"#9CA3AF", border:"rgba(0,0,0,0.08)" };
  return (
    <span style={{
      background: c.bg,
      color: c.txt,
      padding: "3px 9px 3px 7px",
      borderRadius: 99,
      fontSize: 9.5,
      fontWeight: 800,
      letterSpacing: 0.3,
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      border: `1px solid ${c.border}`,
      whiteSpace: "nowrap",
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    }}>
      <span style={{width:5, height:5, borderRadius:"50%", background:c.dot, display:"inline-block", flexShrink:0}}/>
      {status}
    </span>
  );
};

/* ─── TICKET DETAILS VIEW ─────────────────────────────────────────────────── */
const TicketDetailsView = ({ ticket, buildImageUrl, buildProfileUrl }) => (
  <div style={s.detailsContent}>
    <style>{`
      @keyframes slideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      .assign-card:hover { border-color: #D4AF37 !important; box-shadow: 0 4px 16px rgba(212,175,55,0.12) !important; }
    `}</style>
    <div style={{animation:"slideUp 0.28s ease"}}>

      {/* Details Header */}
      <div style={s.detailsHeader}>
        <div style={s.detailsHeaderLeft}>
          <div style={s.detailsCodeWrap}>
            <div style={s.detailsCodeDot}/>
            <span style={s.detailsCode}>{ticket.ticket_code}</span>
          </div>
          <div style={s.detailsDate}>
            <FiClock size={10} color="#94A3B8"/>
            {new Date(ticket.created_at).toLocaleDateString("en-IN", {
              weekday:"short", day:"2-digit", month:"short", year:"numeric",
              hour:"2-digit", minute:"2-digit"
            })}
          </div>
        </div>
        <StatusBadge status={ticket.status}/>
      </div>

      {/* Requestor */}
      <DetailSection title="Requestor" icon={<FiUser size={12}/>} accent="#D4AF37">
        <div style={s.userCard}>
          <div style={s.userAvatar}>
            {buildProfileUrl(ticket.user_image)
              ? <img src={buildProfileUrl(ticket.user_image)} alt=""
                  style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:14}}
                  onError={e=>{e.target.style.display="none";}}/>
              : <span style={{fontSize:20,fontWeight:900,color:"#FFF"}}>
                  {(ticket.user_name||"U").charAt(0).toUpperCase()}
                </span>
            }
          </div>
          <div style={{flex:1, minWidth:0}}>
            <div style={s.userName}>{ticket.user_name}</div>
            <div style={s.userDetailsGrid}>
              <div style={s.userDetailItem}><FiMail size={10} color="#94A3B8"/> <span>{ticket.user_email}</span></div>
              <div style={s.userDetailItem}><FiPhone size={10} color="#94A3B8"/> <span>{ticket.user_phone}</span></div>
              {ticket.user_department && <div style={s.userDetailItem}><FiBriefcase size={10} color="#94A3B8"/> <span>{ticket.user_department}</span></div>}
              {ticket.floor_number && <div style={s.userDetailItem}><FiMapPin size={10} color="#94A3B8"/> <span>Floor {ticket.floor_number}, Cabin {ticket.cabin_number}</span></div>}
            </div>
          </div>
        </div>
      </DetailSection>

      {/* Issue Details */}
      <DetailSection title="Issue Details" icon={<FiAlertCircle size={12}/>} accent="#3B82F6">
        <div style={s.issueGrid}>
          <div>
            <div style={s.detailLabel}>Category</div>
            <div style={s.detailValue}>{ticket.category_name || "—"}</div>
          </div>
          <div>
            <div style={s.detailLabel}>Sub-Category</div>
            <div style={s.detailValue}>{ticket.subcategory_name || "—"}</div>
          </div>
        </div>
        <div style={{marginTop:12}}>
          <div style={s.detailLabel}>Description</div>
          <div style={s.descriptionBox}>{ticket.description || "—"}</div>
        </div>
        {ticket.scanned_location && (
          <div style={{marginTop:10}}>
            <div style={s.detailLabel}>Scanned Location</div>
            <div style={{...s.detailValue, display:"flex", alignItems:"center", gap:6}}>
              <FiMapPin size={11} color="#D4AF37"/> {ticket.scanned_location}
            </div>
          </div>
        )}
      </DetailSection>

      {/* Media / Attachments */}
      {(ticket.image || ticket.video || ticket.audio) && (
        <DetailSection title="Attachments" icon={<FiImage size={12}/>} accent="#8B5CF6">
          <div style={s.mediaGrid}>
            {ticket.image && (
              <a className="media-card" href={buildImageUrl(ticket.image)} target="_blank" rel="noreferrer" style={s.mediaCard}>
                <div style={{...s.mediaIconWrap, background:"linear-gradient(135deg,#FEF3C7,#FDE68A)"}}>
                  <FiImage size={18} color="#F59E0B"/>
                </div>
                <div style={s.mediaLabel}>Image</div>
                <div style={s.mediaAction}>View →</div>
              </a>
            )}
            {ticket.video && (
              <a className="media-card" href={buildImageUrl(ticket.video)} target="_blank" rel="noreferrer" style={s.mediaCard}>
                <div style={{...s.mediaIconWrap, background:"linear-gradient(135deg,#FEE2E2,#FECACA)"}}>
                  <FiPlayCircle size={18} color="#EF4444"/>
                </div>
                <div style={s.mediaLabel}>Video</div>
                <div style={s.mediaAction}>View →</div>
              </a>
            )}
            {ticket.audio && (
              <a className="media-card" href={buildImageUrl(ticket.audio)} target="_blank" rel="noreferrer" style={s.mediaCard}>
                <div style={{...s.mediaIconWrap, background:"linear-gradient(135deg,#DBEAFE,#BFDBFE)"}}>
                  <FiMic size={18} color="#3B82F6"/>
                </div>
                <div style={s.mediaLabel}>Audio</div>
                <div style={s.mediaAction}>View →</div>
              </a>
            )}
          </div>
        </DetailSection>
      )}

      {/* Assignment */}
      {(ticket.hod_name || ticket.worker_name) && (
        <DetailSection title="Assignment" icon={<FiUserCheck size={12}/>} accent="#10B981">
          <div style={{display:"flex", flexDirection:"column", gap:10}}>
            {ticket.hod_name && (
              <div className="assign-card" style={s.assignCard}>
                <div style={s.assignCardHeader}>
                  <div style={{...s.assignBadge, background:"linear-gradient(135deg,#D1FAE5,#A7F3D0)", color:"#065F46"}}>
                    <FiUserCheck size={9}/> HOD
                  </div>
                  <div style={s.assignName}>{ticket.hod_name}</div>
                </div>
                <div style={s.assignContacts}>
                  <div style={s.assignContact}><FiMail size={10} color="#94A3B8"/> {ticket.hod_email}</div>
                  <div style={s.assignContact}><FiPhone size={10} color="#94A3B8"/> {ticket.hod_phone}</div>
                </div>
              </div>
            )}
            {ticket.worker_name && (
              <div className="assign-card" style={s.assignCard}>
                <div style={s.assignCardHeader}>
                  <div style={{...s.assignBadge, background:"linear-gradient(135deg,#EDE9FE,#DDD6FE)", color:"#4C1D95"}}>
                    <FiTool size={9}/> Worker
                  </div>
                  <div style={s.assignName}>{ticket.worker_name}</div>
                </div>
                <div style={s.assignContacts}>
                  <div style={s.assignContact}><FiPhone size={10} color="#94A3B8"/> {ticket.worker_phone}</div>
                  <div style={s.assignContact}><FiTool size={10} color="#94A3B8"/> {ticket.active_tasks || 0} active tasks</div>
                </div>
              </div>
            )}
          </div>
        </DetailSection>
      )}

      {/* Remarks */}
      {ticket.remarks && (
        <DetailSection title="Remarks" icon={<FiMessageSquare size={12}/>} accent="#3B82F6">
          <div style={s.remarksBox}>{ticket.remarks}</div>
        </DetailSection>
      )}

      {/* Feedback */}
      {ticket.rating_status === 1 && (
        <DetailSection title="Feedback" icon={<FiStar size={12}/>} accent="#F59E0B">
          <div style={s.ratingRow}>
            <div style={{display:"flex", gap:3}}>
              {[...Array(5)].map((_, i) => (
                <FiStar key={i} size={15}
                  color={i < ticket.rating ? "#F59E0B" : "#E2E8F0"}
                  fill={i < ticket.rating ? "#F59E0B" : "none"}
                  style={{filter: i < ticket.rating ? "drop-shadow(0 1px 3px rgba(245,158,11,0.4))" : "none"}}
                />
              ))}
            </div>
            <span style={s.ratingText}>{ticket.rating} / 5</span>
          </div>
          {ticket.feedback && <div style={s.feedbackBox}>{ticket.feedback}</div>}
        </DetailSection>
      )}

    </div>
  </div>
);

/* ─── DETAIL SECTION ──────────────────────────────────────────────────────── */
const DetailSection = ({ title, icon, children, accent = "#D4AF37" }) => (
  <div className="detail-section-card" style={s.detailSection}>
    <div style={s.detailSectionTitle}>
      <div style={{...s.detailSectionIcon, color: accent}}>{icon}</div>
      <span>{title}</span>
    </div>
    <div style={s.detailSectionBody}>{children}</div>
  </div>
);

/* ─── DETAIL ROW ──────────────────────────────────────────────────────────── */
const DetailRow = ({ label, value, isLong }) => (
  <div style={{marginBottom: isLong ? 12 : 8}}>
    <div style={s.detailLabel}>{label}</div>
    <div style={{...s.detailValue, whiteSpace: isLong ? "pre-wrap" : "normal"}}>{value || "—"}</div>
  </div>
);

/* ─── STYLES ──────────────────────────────────────────────────────────────── */
const s = {
  layout: {
    display: "flex",
    background: "#F0F3FA",
    minHeight: "100vh",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  main: {
    marginLeft: 280,
    padding: "28px 32px",
    flex: 1,
    minWidth: 0,
  },

  // ── Header
  header: {
    marginBottom: 22,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  headerLeft: {},
  breadcrumb: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: "#A3AED0",
    fontWeight: 600,
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 900,
    color: "#1B2559",
    letterSpacing: "-0.6px",
  },
  subtitle: {
    margin: "3px 0 0",
    fontSize: 12.5,
    color: "#94A3B8",
    fontWeight: 500,
  },
  headerBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#FFF",
    border: "1px solid #E9EDF7",
    borderRadius: 99,
    padding: "6px 14px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
    marginTop: 4,
  },
  headerBadgeDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#10B981",
    boxShadow: "0 0 0 3px rgba(16,185,129,0.2)",
    animation: "pulse 2s ease infinite",
  },

  // ── Search Card
  searchCard: {
    background: "#FFF",
    borderRadius: 18,
    padding: "20px",
    border: "1px solid #E9EDF7",
    boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
    marginBottom: 18,
  },
  searchRow: {
    display: "flex",
    gap: 10,
    marginBottom: 0,
  },
  searchBox: {
    flex: 1,
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#A3AED0",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    padding: "12px 44px",
    borderRadius: 12,
    border: "1.5px solid #E9EDF7",
    outline: "none",
    fontSize: 13,
    color: "#1B2559",
    fontFamily: "inherit",
    fontWeight: 500,
    background: "#FAFBFF",
    transition: "all 0.2s ease",
    boxSizing: "border-box",
  },
  clearXBtn: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "#F1F5F9",
    border: "none",
    color: "#64748B",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
  },
  searchBtn: {
    padding: "12px 22px",
    borderRadius: 12,
    border: "none",
    background: "#1B2559",
    color: "#FFF",
    fontWeight: 700,
    fontSize: 12.5,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 7,
    transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
    boxShadow: "0 4px 16px rgba(27,37,89,0.25)",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
  },

  filterDivider: {
    height: 1,
    background: "linear-gradient(90deg, transparent, #E9EDF7 20%, #E9EDF7 80%, transparent)",
    margin: "16px 0",
  },

  filterRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 12,
    alignItems: "end",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  filterLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#94A3B8",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  filterSelect: {
    padding: "9px 12px",
    borderRadius: 10,
    border: "1.5px solid #E9EDF7",
    outline: "none",
    fontSize: 12,
    color: "#475569",
    fontFamily: "inherit",
    fontWeight: 600,
    background: "#FAFBFF",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  filterDate: {
    padding: "9px 12px",
    borderRadius: 10,
    border: "1.5px solid #E9EDF7",
    outline: "none",
    fontSize: 12,
    color: "#475569",
    fontFamily: "inherit",
    fontWeight: 600,
    background: "#FAFBFF",
    transition: "all 0.2s ease",
  },
  clearFilterBtn: {
    padding: "9px 16px",
    borderRadius: 10,
    border: "1.5px solid #E9EDF7",
    background: "#FAFBFF",
    color: "#64748B",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontFamily: "inherit",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },

  // ── Content Grid
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "370px 1fr",
    gap: 14,
    minHeight: "calc(100vh - 360px)",
  },

  // ── List Panel
  listPanel: {
    background: "#FFF",
    borderRadius: 18,
    border: "1px solid #E9EDF7",
    boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  listHeader: {
    padding: "14px 16px",
    borderBottom: "1px solid #F0F4FA",
    background: "#FAFBFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  listHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  listTitle: {
    fontSize: 12.5,
    fontWeight: 800,
    color: "#1B2559",
  },
  listCount: {
    background: "linear-gradient(135deg,#1B2559,#2D3A7A)",
    color: "#FFF",
    fontSize: 10,
    fontWeight: 800,
    padding: "2px 8px",
    borderRadius: 99,
    minWidth: 22,
    textAlign: "center",
  },
  listSelected: {
    fontSize: 10,
    color: "#10B981",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  ticketList: {
    flex: 1,
    overflowY: "auto",
    padding: "10px",
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },

  // Ticket Card
  ticketCard: {
    padding: "12px 13px",
    borderRadius: 12,
    marginBottom: 5,
    cursor: "pointer",
    border: "1px solid #F0F4FA",
    background: "#FFF",
    position: "relative",
    overflow: "hidden",
  },
  ticketCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 7,
  },
  ticketCodeBadge: {
    background: "linear-gradient(135deg,#1B2559 0%,#2D3A7A 100%)",
    borderRadius: 6,
    padding: "3px 8px",
  },
  ticketCodeText: {
    fontSize: 10,
    fontWeight: 800,
    color: "#FFF",
    fontFamily: "monospace",
    letterSpacing: 0.5,
  },
  ticketDesc: {
    fontSize: 11.5,
    color: "#475569",
    fontWeight: 500,
    marginBottom: 8,
    lineHeight: 1.5,
  },
  ticketMeta: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 10,
    color: "#94A3B8",
    fontWeight: 600,
  },
  ticketMetaItem: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    maxWidth: 100,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  ticketMetaDate: {
    marginLeft: "auto",
    fontSize: 9.5,
    fontWeight: 700,
    color: "#CBD5E1",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 280,
    textAlign: "center",
    padding: 32,
  },
  emptyIcon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: "linear-gradient(135deg,#FEF3C7,#FDE68A)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
    boxShadow: "0 4px 16px rgba(212,175,55,0.2)",
  },
  emptyTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#1B2559",
    marginBottom: 4,
  },
  emptySubtitle: {
    fontSize: 11,
    color: "#94A3B8",
    fontWeight: 500,
  },

  // ── Details Panel
  detailsPanel: {
    background: "#FFF",
    borderRadius: 18,
    border: "1px solid #E9EDF7",
    boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  detailsContent: {
    flex: 1,
    overflowY: "auto",
    padding: "22px",
  },

  loadingState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: 14,
  },
  spinnerRing: {
    width: 38,
    height: 38,
    border: "2.5px solid #E9EDF7",
    borderTopColor: "#D4AF37",
    borderRadius: "50%",
    animation: "spin 0.75s linear infinite",
  },
  loadingText: {
    fontSize: 12,
    color: "#94A3B8",
    fontWeight: 600,
  },

  placeholderState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: 40,
    textAlign: "center",
  },
  placeholderIcon: {
    width: 60,
    height: 60,
    borderRadius: 18,
    background: "linear-gradient(135deg,#FEF3C7,#FDE68A)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    boxShadow: "0 6px 24px rgba(212,175,55,0.2)",
  },
  placeholderTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: "#1B2559",
    marginBottom: 6,
  },
  placeholderSubtitle: {
    fontSize: 12,
    color: "#94A3B8",
    fontWeight: 500,
    maxWidth: 220,
    lineHeight: 1.6,
  },

  // ── Details Header
  detailsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1.5px solid #F0F4FA",
  },
  detailsHeaderLeft: {},
  detailsCodeWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 5,
  },
  detailsCodeDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#D4AF37,#F5D76E)",
    boxShadow: "0 0 0 3px rgba(212,175,55,0.2)",
  },
  detailsCode: {
    fontSize: 15,
    fontWeight: 900,
    color: "#1B2559",
    fontFamily: "monospace",
    letterSpacing: 0.5,
  },
  detailsDate: {
    fontSize: 11,
    color: "#94A3B8",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  // ── Detail Section
  detailSection: {
    marginBottom: 16,
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid #F0F4FA",
  },
  detailSectionTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: "#1B2559",
    textTransform: "uppercase",
    letterSpacing: 0.7,
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "10px 16px",
    background: "#FAFBFF",
    borderBottom: "1px solid #F0F4FA",
  },
  detailSectionIcon: {
    display: "flex",
    alignItems: "center",
  },
  detailSectionBody: {
    padding: "14px 16px",
    background: "#FFF",
  },

  detailLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#94A3B8",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  detailValue: {
    fontSize: 13,
    color: "#1B2559",
    fontWeight: 600,
  },

  issueGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  descriptionBox: {
    fontSize: 13,
    color: "#475569",
    fontWeight: 500,
    lineHeight: 1.7,
    background: "#F8FAFC",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid #F0F4FA",
    whiteSpace: "pre-wrap",
  },

  // ── User Card
  userCard: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
  },
  userAvatar: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: "linear-gradient(135deg,#D4AF37,#F5D76E)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
    boxShadow: "0 4px 16px rgba(212,175,55,0.3)",
  },
  userName: {
    fontSize: 13.5,
    fontWeight: 800,
    color: "#1B2559",
    marginBottom: 6,
  },
  userDetailsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  userDetailItem: {
    fontSize: 11,
    color: "#64748B",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  // ── Media
  mediaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
  },
  mediaCard: {
    padding: "14px 10px",
    background: "#FAFBFF",
    borderRadius: 12,
    textAlign: "center",
    border: "1.5px solid #F0F4FA",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    textDecoration: "none",
    transition: "all 0.2s ease",
    cursor: "pointer",
  },
  mediaIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  mediaLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#475569",
  },
  mediaAction: {
    fontSize: 10,
    fontWeight: 700,
    color: "#D4AF37",
  },

  // ── Assignment
  assignCard: {
    background: "#FAFBFF",
    padding: "13px 14px",
    borderRadius: 12,
    border: "1.5px solid #F0F4FA",
    transition: "all 0.2s ease",
  },
  assignCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  assignBadge: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 9.5,
    fontWeight: 800,
    padding: "3px 8px",
    borderRadius: 99,
    letterSpacing: 0.3,
    flexShrink: 0,
  },
  assignName: {
    fontSize: 12.5,
    fontWeight: 800,
    color: "#1B2559",
  },
  assignContacts: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  assignContact: {
    fontSize: 10.5,
    color: "#64748B",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  // ── Remarks & Feedback
  remarksBox: {
    fontSize: 12.5,
    color: "#475569",
    fontWeight: 500,
    lineHeight: 1.7,
    background: "linear-gradient(135deg,#EFF6FF,#DBEAFE)",
    padding: "12px 14px",
    borderRadius: 10,
    borderLeft: "3px solid #3B82F6",
  },
  ratingRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 10,
  },
  ratingText: {
    fontSize: 13,
    fontWeight: 800,
    color: "#1B2559",
    background: "linear-gradient(135deg,#FEF3C7,#FDE68A)",
    padding: "3px 10px",
    borderRadius: 99,
    border: "1px solid rgba(245,158,11,0.2)",
  },
  feedbackBox: {
    fontSize: 12.5,
    color: "#475569",
    fontWeight: 500,
    lineHeight: 1.7,
    background: "linear-gradient(135deg,#FFFBEB,#FEF3C7)",
    padding: "12px 14px",
    borderRadius: 10,
    borderLeft: "3px solid #F59E0B",
  },
};

export default AdminTicketSearch;